import { useForm } from 'react-hook-form';
import { useState, useEffect, useContext } from 'react';
import firebaseService from '@services/firebase';
import FormResetPassword from './FormResetPassword';
import FormSignIn from './FormSignIn';
import css from 'styled-jsx/css';
import BreadcrumbList from '@components/BreadcrumbList';
import { useRedirect } from '@utils/redirect';
import { getLegoUrl } from '@utils/config';
import { getQueryParam } from '@utils/query';
import { LayoutContext } from '@context/LayoutContext';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import Link from 'next/link';
import { useTranslations } from 'use-intl';
import getConfig from 'next/config';
import { dataLayerTrackEvent } from '@utils/tracking';
import { useReferralContext } from '@context/referral';
import { trackEvent } from '@monofront/tracking';
import { useRouter } from 'next/router';
import {
  FacebookAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  getAdditionalUserInfo,
  getAuth,
  linkWithCredential,
  signInWithCustomToken,
  signInWithPopup,
} from '@firebase/auth';

const formCss = css`
  .error {
    margin-bottom: 22px;
  }

  .form > h4 {
    margin: 46px 0 12px 0;
  }

  .form :global(input) {
    margin: var(--space-3) 0;
  }

  .form :global(input[type='password']) {
    padding-right: var(--space-7);
  }

  .link-signup {
    display: flex;
    justify-content: center;
    margin-bottom: 14px;
  }

  .form > :global(button) {
    margin: var(--space-4) 0 var(--space-5) 0;
  }

  .message {
    margin-top: 22px;
    font-weight: var(--font-weight-bold);
  }

  .input-password {
    position: relative;
  }

  .eye {
    position: absolute;
    right: 17px;
    top: 9px;
    height: 24px;
    z-index: var(--zindex-form);
    cursor: pointer;
    transition: 0.3s;
  }
`;

const handleAccountExists = async (error) => {
  if (error.credential.providerId === 'facebook.com') {
    return await handleFacebookAuthFailure(error);
  }
};

const handleFacebookAuthFailure = async (error) => {
  const response = await fetch(getLegoUrl() + '/facebooktoken', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
    },
    body: JSON.stringify({
      email: error.email,
      credential: error.credential.accessToken,
    }),
  });
  const { token } = await response.json();

  if (200 !== response.status) {
    return;
  }

  return token;
};

const {
  publicRuntimeConfig: { authProviders },
} = getConfig();

const SignIn = () => {
  const [isResetPasswordForm, toggleResetPasswordForm] =
    useState<boolean>(false);
  const [resetPasswordFormMessage, setResetPasswordFormMessage] =
    useState<string>('');
  const { setCustomTitleHeaderMobile } = useContext(LayoutContext);
  const {
    formState: { errors },
    setError,
  } = useForm();
  const { redirectToPaymentPageIfNewUser, redirectToSubscriptionSummary } =
    useRedirect();
  const { isFromReferral } = useReferralContext();

  const router = useRouter();
  const email = router.query.email as string | undefined;

  const tAccount = useTranslations('Account');
  const tErrors = useTranslations('Errors');

  const handleExternalSignIn = async (provider) => {
    try {
      const userCredential = await signInWithPopup(
        getAuth(firebaseService.app),
        provider,
      );

      const additionalUserInfo = getAdditionalUserInfo(userCredential);

      if (additionalUserInfo?.isNewUser) {
        const isMaap = getQueryParam('isMaap') === 'true';

        // deprecated tracker https://gitlab.com/la-fourche/scrum/-/issues/1968
        dataLayerTrackEvent('accountCreated', {
          isMaap,
        });

        trackEvent({
          type: 'LFSignUp',
          data: {
            method: additionalUserInfo.providerId ?? '',
            isMaap,
          },
        });
      }

      if (isFromReferral) {
        await redirectToSubscriptionSummary();
      } else {
        await redirectToPaymentPageIfNewUser(userCredential);
      }
    } catch (error: any) {
      if (error.code === 'auth/account-exists-with-different-credential') {
        const firebaseToken = await handleAccountExists(error);

        if (firebaseToken) {
          const userCredential = await signInWithCustomToken(
            getAuth(firebaseService.app),
            firebaseToken,
          );
          const additionalUserInfo = getAdditionalUserInfo(userCredential);

          if (additionalUserInfo?.isNewUser) {
            const isMaap = getQueryParam('isMaap') === 'true';

            // deprecated tracker https://gitlab.com/la-fourche/scrum/-/issues/1968
            dataLayerTrackEvent('accountCreated', {
              isMaap,
            });

            trackEvent({
              type: 'LFSignUp',
              data: {
                method: additionalUserInfo.providerId ?? 'unknown',
                isMaap,
              },
            });
          }
          await linkWithCredential(userCredential.user, error.credential);
          redirectToPaymentPageIfNewUser(userCredential);
        }
      }
      setError('signInWithPopup', error.code, error.message);
      throw new Error(error);
    }
  };

  useEffect(() => {
    if (getQueryParam('resetPasswordError') === 'expiration') {
      toggleResetPasswordForm(true);
      setResetPasswordFormMessage(tAccount('resetPasswordLinkExpired'));
    }
    setCustomTitleHeaderMobile!(tAccount('logIn'));
  }, []);

  return (
    <>
      <BreadcrumbList name="Compte" />
      <section className="container">
        <div className="content">
          <h1
            className="tw-lf-title desktop:tw-lf-title-base tw-lf-title-sm tw-mb-5 tw-text-center desktop:tw-mb-6"
            data-testid="login-title"
          >
            {tAccount('logIn')}
          </h1>
          {errors.signInWithPopup && (
            <div className="error">
              <p className="error tw-lf-error-message">
                {errors.signInWithPopup.type ===
                'auth/account-exists-with-different-credential'
                  ? tAccount('accountAlreadyExists')
                  : tErrors('aProblemOccured')}
              </p>
            </div>
          )}
          {!isResetPasswordForm ? (
            <>
              {authProviders.includes('email') && (
                <>
                  <FormSignIn
                    toggleResetPasswordForm={toggleResetPasswordForm}
                    formCss={formCss}
                    defaultValues={{ email }}
                  />
                  <div className="separator">
                    <div className="line" />
                    <p>{tAccount('orContinueWith')}</p>
                    <div className="line" />
                  </div>
                </>
              )}
              <div className="social-signin">
                {authProviders.includes('facebook') && (
                  <button
                    className="social-button"
                    type="button"
                    onClick={() =>
                      handleExternalSignIn(new FacebookAuthProvider())
                    }
                  >
                    <ResponsiveImage
                      src="/icons/facebook.svg"
                      alt="facebook"
                      width={30}
                      height={30}
                    />
                  </button>
                )}
                {authProviders.includes('google') && (
                  <button
                    className="social-button"
                    type="button"
                    onClick={() =>
                      handleExternalSignIn(new GoogleAuthProvider())
                    }
                  >
                    <ResponsiveImage
                      src="/icons/google.svg"
                      alt="google"
                      width={30}
                      height={30}
                    />
                  </button>
                )}
                {authProviders.includes('apple') && (
                  <button
                    className="social-button"
                    type="button"
                    onClick={() =>
                      handleExternalSignIn(new OAuthProvider('apple.com'))
                    }
                  >
                    <ResponsiveImage
                      src="/icons/apple.svg"
                      alt="apple"
                      width={30}
                      height={30}
                    />
                  </button>
                )}
              </div>
            </>
          ) : (
            <FormResetPassword
              toggleResetPasswordForm={toggleResetPasswordForm}
              formCss={formCss}
              message={resetPasswordFormMessage}
            />
          )}

          <div className="link-signup">
            <p className="no-account">
              {tAccount('noAccountYet')}{' '}
              <Link href="/account/register/flow" legacyBehavior>
                <a className="tw-lf-link-md" rel="nofollow">
                  {tAccount('register')}
                </a>
              </Link>
            </p>
          </div>
        </div>
      </section>
      <style jsx>{`
        .container {
          margin: 32px 0;
          padding: var(--padding-layout-l);
        }

        .container > :global(div) {
          position: relative;
          overflow: visible;
        }

        .content {
          position: relative;
          width: 430px;
          margin: 0 auto;
          text-align: center;
          padding: var(--space-2) 0;
        }

        .social-signin {
          display: flex;
          flex-direction: row;
          justify-content: center;
          margin-bottom: var(--space-7);
          gap: var(--space-3);
        }

        .social-button {
          width: 96px;
          height: 60px;
          border: 1px solid var(--clr-tertiary-2);
          border-radius: var(--rd-3);
          background-color: transparent;
          cursor: pointer;
        }

        .error {
          margin-bottom: 22px;
        }

        .separator {
          display: flex;
          align-items: center;
          justify-content: center;
          margin: var(--space-6) 0;
        }

        .separator > p {
          margin: 0 var(--space-2);
        }

        .line {
          height: 1px;
          background-color: var(--black);
          flex: 1;
        }

        .no-account {
          color: var(--clr-grey-4);
        }

        @media (max-width: 800px) {
          .container {
            padding: 0;
            margin: 0;
          }

          .content {
            width: 100%;
            padding: 22px 10px;
          }
        }
      `}</style>
    </>
  );
};

export default SignIn;
